import { CheckOutlined, CloseCircleFilled, EditOutlined } from "@ant-design/icons";
import { Form, Popconfirm, Space, Table, Tag, Tooltip, message } from "antd";

import { dictionaryApi } from "api/dictionary.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { ActionButton, TableLayout, TableLayoutFooter, getTableShowingRange } from "components/table";
import { useLead } from "hooks/useLead";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Lead, LeadStatus, LeadStatusTrans } from "types/lead";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { formatDateTime } from "utils/date";
import { ContactModal } from "./components/ContactModal";
import { leadApi } from "api/lead.api";
// import { StaffModal } from "./components/StaffModal";

const { Column } = Table;

export const ContactPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const { leads, fetchData, loading, query, total } = useLead({
    initQuery: { page: 1, limit: 10 },
  });

  const [loadingSolve, setLoadingSolve] = useState(false);

  const modalRef = useRef<ContactModal>();

  const debounceSearch = useCallback(
    debounce((keyword: string) => {
      query.search = keyword;
      query.page = 1;
      fetchData();
    }, 300),
    [query, fetchData],
  );

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSolve = async (id: number) => {
    setLoadingSolve(true);
    try {
      await leadApi.solve(id, { status: LeadStatus.Solve });
      message.success("Cập nhật thành công");
      fetchData();
    } finally {
      setLoadingSolve(false);
    }
  };

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div>
      <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: search,
                placeholder: "Tìm kiếm",
                onChange: (value) => {
                  setSearch(value);
                  debounceSearch(value);
                },
                inputProps: {
                  allowClear: {
                    clearIcon: (
                      <CloseCircleFilled
                        onClick={() => {
                          setSearch("");
                          query.search = "";
                          fetchData();
                        }}
                      />
                    ),
                  },
                },
              }}
              filterLabel={null}
            />
          }
          table={
            <AppTable<Lead> loading={loading} pagination={false} rowKey="id" dataSource={leads}>
              <Column title="Họ và tên" dataIndex="name" key="name" />
              <Column title="Email" dataIndex="email" key="email" />
              <Column title="Số điện thoại" dataIndex="phone" key="phone" />
              <Column title="Nội dung" dataIndex="content" key="content" />
              <Column title="Ghi chú" dataIndex="note" key="note" />
              <Column
                title="Trạng thái"
                dataIndex="status"
                key="status"
                render={(status: LeadStatus) => {
                  return <Tag color={LeadStatusTrans[status]?.color}>{LeadStatusTrans[status]?.label}</Tag>;
                }}
              />
              <Column
                title="Ngày tạo"
                dataIndex="createdAt"
                render={(text) => <span>{formatDateTime(text)}</span>}
              />

              <Column
                title="Thao tác"
                key="action"
                width={100}
                align="center"
                render={(text, record: Lead) => (
                  <div className="flex justify-center items-center gap-2">
                    <Tooltip title="Đánh dấu đã xử lý">
                      <Popconfirm
                        placement="top"
                        title={"Xác nhận đã xử lý liên hệ này?"}
                        onConfirm={() => handleSolve(record.id)}
                        okText="Xác nhận"
                        cancelText="Không"
                        disabled={record.status === LeadStatus.Solve}
                      >
                        <ActionButton
                          aria-label="Đánh dấu đã xử lý"
                          disabled={record.status === LeadStatus.Solve}
                          loading={loadingSolve}
                        >
                          <CheckOutlined style={{ color: "#22c55e" }} />
                        </ActionButton>
                      </Popconfirm>
                    </Tooltip>

                    <Tooltip title="Chỉnh sửa">
                      <ActionButton
                        aria-label="Chỉnh sửa"
                        onClick={() => {
                          modalRef.current?.handleUpdate(record);
                        }}
                      >
                        <EditOutlined style={{ color: "#6c84e5" }} />
                      </ActionButton>
                    </Tooltip>
                  </div>
                )}
              />
            </AppTable>
          }
          footer={
            <TableLayoutFooter
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              currentPage={query.page}
              pageSize={query.limit}
              onChange={({ limit, page }) => {
                query.page = page;
                query.limit = limit;
                fetchData();
              }}
            />
          }
        />

      <ContactModal onSubmit={fetchData} onClose={() => {}} ref={modalRef} />
    </div>
  );
});
